import { useMemo } from "react";
import { useImmer } from "use-immer";

import {
  AnalyzeTinybirdResourceResponseBodyType,
  GraphConfig,
  STANDARD_EVENTS_ALLOWED_KEY_METRICS,
} from "@intelligems/ig-types";
import {
  shouldShowProfitData,
  shouldShowProfitPlaceholder,
} from "@src/features/analytics/utils";
import { MobileAnalyticsFrame } from "@src/features/mobileAnalytics/shared/components/MobileAnalyticsFrame";
import { MobileTimeSeriesGraphWrapper } from "@src/features/mobileAnalytics/testDetails/components/MobileTimeSeriesGraphWrapper";
import { useGenerateGraphConfig } from "@src/features/mobileAnalytics/testDetails/hooks/useGenerateGraphConfig";
import { UseQueryResult } from "@tanstack/react-query";

const isProfitRelatedStandardMetric = (metricId: string): boolean => {
  for (const [key, value] of Object.entries(
    STANDARD_EVENTS_ALLOWED_KEY_METRICS
  )) {
    if (
      value.analyticsMetricId === metricId &&
      key.toLowerCase().includes("profit")
    ) {
      return true;
    }
  }
  return false;
};

interface MobileTimeSeriesProps {
  variationOverviewData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  graphConfigs: GraphConfig[];
  overviewDataLoaded: boolean;
}

export const MobileTimeSeries = ({
  variationOverviewData,
  graphConfigs,
  overviewDataLoaded,
}: MobileTimeSeriesProps) => {
  const generatedOverviewGraphConfig = useGenerateGraphConfig();

  // Generate dropdown options for metric selection
  const dropdownOptions = generatedOverviewGraphConfig.map((config) => ({
    title: config.title,
    id: config.impactMetric || config.metrics[0] || "",
  }));

  const shouldShowProfit = useMemo(() => {
    return shouldShowProfitData(variationOverviewData[0]?.data?.metrics || []);
  }, [variationOverviewData]);

  const filteredDropdownOptions = useMemo(() => {
    if (shouldShowProfit === true) {
      return dropdownOptions;
    }
    return dropdownOptions.filter(
      (option) => !isProfitRelatedStandardMetric(option.id)
    );
  }, [dropdownOptions, shouldShowProfit]);

  const filteredConfigs = useMemo(() => {
    return graphConfigs.filter((config) => {
      return !shouldShowProfitPlaceholder(shouldShowProfit, config);
    });
  }, [graphConfigs, shouldShowProfit]);

  const timeSeriesConfigs = useMemo(
    () => filteredConfigs.filter((config) => config.hasTimeseries),
    [filteredConfigs]
  );

  const getInitialId = () => {
    const firstTimeSeriesConfig = timeSeriesConfigs[0];
    if (!firstTimeSeriesConfig) {
      return filteredDropdownOptions[0]?.id || "";
    }
    return (
      firstTimeSeriesConfig.impactMetric ||
      firstTimeSeriesConfig.metrics[0] ||
      ""
    );
  };

  const [selectedMetricId, setSelectedMetricId] =
    useImmer<string>(getInitialId());

  if (!timeSeriesConfigs.length) {
    return null;
  }

  return (
    <MobileAnalyticsFrame
      title="Time Series"
      selectedValue={selectedMetricId}
      onSelectionChange={setSelectedMetricId}
      dropdownOptions={filteredDropdownOptions}
    >
      <MobileTimeSeriesGraphWrapper
        graphConfigs={timeSeriesConfigs}
        selectedMetricId={selectedMetricId}
        overviewDataLoaded={overviewDataLoaded}
      />
    </MobileAnalyticsFrame>
  );
};
